import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/** e.g. desk + 1 → "desk-1"; Lane + 1 → "Lane 1" */
const formatZoneLabel = (noun, key) => {
  const n = String(noun || 'Lane').trim() || 'Lane';
  const k = String(key ?? '').trim();
  if (!k) return n;
  if (/^workstation$/i.test(n)) return `Workstation-${k}`;
  if (/^desk$/i.test(n)) return `Desk-${k}`;
  return `${n} ${k}`;
};

/**
 * Map a rect from source camera pixels into a letterboxed destination frame
 * (same as ffmpeg scale=…:force_original_aspect_ratio=decrease + center pad).
 */
const mapRectToLetterbox = (rect, srcW, srcH, dstW, dstH) => {
  if (!rect || !(srcW > 0) || !(srcH > 0) || !(dstW > 0) || !(dstH > 0)) {
    return rect;
  }
  const srcAspect = srcW / srcH;
  const dstAspect = dstW / dstH;
  let renderedW;
  let renderedH;
  if (srcAspect > dstAspect) {
    renderedW = dstW;
    renderedH = dstW / srcAspect;
  } else {
    renderedH = dstH;
    renderedW = dstH * srcAspect;
  }
  const padX = (dstW - renderedW) / 2;
  const padY = (dstH - renderedH) / 2;
  const sx = renderedW / srcW;
  const sy = renderedH / srcH;
  return {
    x: padX + Number(rect.x) * sx,
    y: padY + Number(rect.y) * sy,
    w: Number(rect.w) * sx,
    h: Number(rect.h) * sy,
  };
};

/** Max x/y extent across parent ROI + lane rects (for inferring missing frame size). */
const maxRoiExtent = (roi, laneRois) => {
  let maxX = 0;
  let maxY = 0;
  const consider = (rect) => {
    if (!rect || typeof rect !== 'object') return;
    const x = Number(rect.x) || 0;
    const y = Number(rect.y) || 0;
    const w = Number(rect.w) || 0;
    const h = Number(rect.h) || 0;
    if (w > 0 && h > 0) {
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    }
  };
  consider(roi);
  if (laneRois && typeof laneRois === 'object') {
    Object.values(laneRois).forEach(consider);
  }
  return { maxX, maxY };
};

/**
 * Resolve ROI reference frame size. Prefer explicit sourceFrameSize; else infer
 * when desk coords clearly exceed the playing video (common RR 1280×720 case).
 */
const resolveSourceFrameSize = (sourceFrameSize, roi, laneRois, naturalW, naturalH) => {
  let srcW = Number(sourceFrameSize?.width) || 0;
  let srcH = Number(sourceFrameSize?.height) || 0;
  if (srcW > 0 && srcH > 0) {
    return { srcW, srcH };
  }
  const { maxX, maxY } = maxRoiExtent(roi, laneRois);
  if (maxX <= naturalW + 2 && maxY <= naturalH + 2) {
    return { srcW: 0, srcH: 0 };
  }
  // Prefer common capture sizes that fully contain the ROI extents.
  const candidates = [
    [1920, 1080],
    [2560, 1440],
    [1280, 720],
    [3840, 2160],
    [640, 480],
  ];
  const fit = candidates.find(([w, h]) => w >= maxX && h >= maxY);
  if (fit) {
    return { srcW: fit[0], srcH: fit[1] };
  }
  return {
    srcW: Math.max(naturalW, Math.ceil(maxX)),
    srcH: Math.max(naturalH, Math.ceil(maxY)),
  };
};

/**
 * Canvas overlay for camera preview (static or live MJPEG).
 * Works with lane_rois alone (no parent roi required) — needed for WORKSTATION desks.
 */
const PreviewRoiOverlay = ({
  imgRef,
  roi = null,
  laneRois = null,
  laneCount = 0,
  zoneNoun = 'Lane',
  active = true,
  /** Map lane key → { occupied, infoLines? } for live occupancy overlay */
  workstationMeta = null,
  /**
   * When ROI coords are in a different space than the playing video (e.g. RR
   * letterboxed 1280×720 from a 1920×1080 camera), pass the ROI source size.
   */
  sourceFrameSize = null,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const hitRegionsRef = useRef([]);
  const [hoveredLaneKey, setHoveredLaneKey] = useState(null);

  const hasLaneRois =
    laneRois && typeof laneRois === 'object' && Object.keys(laneRois).length > 0;
  const hasRoi = roi && Number(roi.w) > 0 && Number(roi.h) > 0;
  const shouldShow = Boolean(active && (hasLaneRois || hasRoi));

  const drawOverlay = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const media = imgRef?.current;
    if (!canvas || !container || !media) return;

    // Support <img> (natural*) and live MSE <video> / host containing a video.
    const el =
      media.tagName === 'VIDEO'
        ? media
        : media.querySelector?.('video') || media;
    const naturalW = el.naturalWidth || el.videoWidth || 0;
    const naturalH = el.naturalHeight || el.videoHeight || 0;
    if (!naturalW || !naturalH) return;

    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    if (!containerW || !containerH) return;

    const imgAspect = naturalW / naturalH;
    const containerAspect = containerW / containerH;
    let renderedW;
    let renderedH;
    if (imgAspect > containerAspect) {
      renderedW = containerW;
      renderedH = containerW / imgAspect;
    } else {
      renderedH = containerH;
      renderedW = containerH * imgAspect;
    }
    const offsetX = (containerW - renderedW) / 2;
    const offsetY = (containerH - renderedH) / 2;
    const scaleX = renderedW / naturalW;
    const scaleY = renderedH / naturalH;

    const { srcW, srcH } = resolveSourceFrameSize(
      sourceFrameSize,
      roi,
      laneRois,
      naturalW,
      naturalH,
    );
    const needsSourceMap =
      srcW > 0 &&
      srcH > 0 &&
      (Math.abs(srcW - naturalW) > 1 || Math.abs(srcH - naturalH) > 1);

    canvas.width = containerW;
    canvas.height = containerH;
    canvas.style.width = `${containerW}px`;
    canvas.style.height = `${containerH}px`;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const hitRegions = [];

    const totalLanes = Number(laneCount) || 0;
    const laneRoiEntries =
      laneRois && typeof laneRois === 'object' ? Object.entries(laneRois) : [];

    const toVideoRect = (rect) =>
      needsSourceMap ? mapRectToLetterbox(rect, srcW, srcH, naturalW, naturalH) : rect;

    const drawInfoBox = (lx, ly, lw, lh, lines, paint = true) => {
      if (!lines.length) return null;
      ctx.font = '10px Arial';
      ctx.textBaseline = 'top';
      const padX = 5;
      const padY = 4;
      const lineH = 13;
      const textW = Math.max(...lines.map((line) => ctx.measureText(line).width));
      const boxW = textW + padX * 2;
      const boxH = lines.length * lineH + padY * 2;
      const margin = 2;
      const preferRight = lx + lw / 2 >= canvas.width / 2;
      const overlapsRoi = (x, y) =>
        x < lx + lw && x + boxW > lx && y < ly + lh && y + boxH > ly;
      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
      let by = clamp(
        ly + (lh - boxH) / 2,
        margin,
        Math.max(margin, canvas.height - boxH - margin),
      );
      let bx = preferRight ? lx + lw : lx - boxW;
      const onCanvas = (x) => x >= margin && x + boxW <= canvas.width - margin;
      if (!onCanvas(bx)) {
        const other = preferRight ? lx - boxW : lx + lw;
        bx = onCanvas(other)
          ? other
          : clamp(bx, margin, Math.max(margin, canvas.width - boxW - margin));
      }
      if (overlapsRoi(bx, by)) {
        const above = ly - boxH;
        const below = ly + lh;
        const cornerX = preferRight ? lx + lw - boxW : lx;
        if (above >= margin) {
          by = above;
          bx = clamp(cornerX, margin, Math.max(margin, canvas.width - boxW - margin));
        } else if (below + boxH <= canvas.height - margin) {
          by = below;
          bx = clamp(cornerX, margin, Math.max(margin, canvas.width - boxW - margin));
        }
      }
      if (paint) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.88)';
        ctx.fillRect(bx, by, boxW, boxH);
        ctx.fillStyle = '#ffffff';
        lines.forEach((line, index) => {
          ctx.fillText(line, bx + padX, by + padY + index * lineH);
        });
      }
      ctx.textBaseline = 'alphabetic';
      return { x: bx, y: by, w: boxW, h: boxH };
    };

    const strokeRect = (rect, color, label, meta, fillColor, laneKey, labelMode = 'compact') => {
      if (!rect || typeof rect !== 'object') return;
      const mapped = toVideoRect(rect);
      const lx = offsetX + Number(mapped.x) * scaleX;
      const ly = offsetY + Number(mapped.y) * scaleY;
      const lw = Number(mapped.w) * scaleX;
      const lh = Number(mapped.h) * scaleY;
      if (!(lw > 0 && lh > 0)) return;
      if (fillColor) {
        ctx.fillStyle = fillColor;
        ctx.fillRect(lx, ly, lw, lh);
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(lx, ly, lw, lh);

      const infoLines = Array.isArray(meta?.infoLines) ? meta.infoLines.filter(Boolean) : [];
      let labelBox = null;
      if (infoLines.length > 0 && labelMode === 'none') {
        labelBox = drawInfoBox(lx, ly, lw, lh, [`${infoLines[0]} …`], false);
      } else if (infoLines.length > 0) {
        const lines = labelMode === 'full' || infoLines.length === 1
          ? infoLines
          : [`${infoLines[0]} …`];
        labelBox = drawInfoBox(lx, ly, lw, lh, lines);
      }
      if (laneKey != null && (labelBox || infoLines.length > 0)) {
        const key = String(laneKey);
        const existing = hitRegions.find((region) => region.key === key);
        if (existing) {
          existing.roi = { x: lx, y: ly, w: lw, h: lh };
          if (labelBox && labelMode === 'full') existing.detail = labelBox;
          else if (labelBox) existing.label = labelBox;
        } else {
          hitRegions.push({
            key,
            roi: { x: lx, y: ly, w: lw, h: lh },
            label: labelMode === 'full' ? null : labelBox,
            detail: labelMode === 'full' ? labelBox : null,
          });
        }
      }
      if (infoLines.length > 0) return;

      const labelX = lx + 4;
      const labelY = ly + 16;
      const durationLabel = meta?.durationLabel;

      ctx.font = 'bold 12px Arial';
      ctx.fillStyle = color;
      ctx.fillText(label, labelX, labelY);

      if (durationLabel) {
        const durationY = labelY + 20;
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = '#ef4444';
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.lineWidth = 4;
        ctx.strokeText(durationLabel, labelX, durationY);
        ctx.fillText(durationLabel, labelX, durationY);
      }

      const cx = lx + lw / 2;
      const cy = ly + lh / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
    };

    if (laneRoiEntries.length > 0) {
      const sortedLanes = laneRoiEntries
        .sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10));
      sortedLanes.forEach(([laneKey, laneRect]) => {
          // Occupied desks: green. Idle / empty (timer stopped): gray.
          // Occupancy preview fills the whole desk. Outline-only when meta is absent.
          const meta = workstationMeta?.[String(laneKey)] || null;
          const occupied =
            workstationMeta == null ? true : Boolean(meta?.occupied);
          const boxColor = occupied ? '#00ff00' : '#9ca3af';
          const fillColor =
            workstationMeta == null
              ? null
              : occupied
                ? 'rgba(0, 255, 0, 0.32)'
                : 'rgba(156, 163, 175, 0.42)';
          const isHovered = String(laneKey) === String(hoveredLaneKey);
          strokeRect(
            laneRect,
            boxColor,
            formatZoneLabel(zoneNoun, laneKey),
            meta,
            fillColor,
            laneKey,
            isHovered ? 'none' : 'compact',
          );
        });
      // Paint the hovered desk's full label last so it sits above neighbors.
      if (hoveredLaneKey != null) {
        const hovered = sortedLanes.find(([laneKey]) => String(laneKey) === String(hoveredLaneKey));
        if (hovered) {
          const [laneKey, laneRect] = hovered;
          const meta = workstationMeta?.[String(laneKey)] || null;
          const occupied = workstationMeta == null ? true : Boolean(meta?.occupied);
          strokeRect(
            laneRect,
            occupied ? '#00ff00' : '#9ca3af',
            formatZoneLabel(zoneNoun, laneKey),
            meta,
            null,
            laneKey,
            'full',
          );
        }
      }
      hitRegionsRef.current = hitRegions;
      return;
    }
    hitRegionsRef.current = hitRegions;

    if (hasRoi && totalLanes <= 1) {
      strokeRect(roi, '#00ff00', 'ROI Zone');
      return;
    }

    if (hasRoi && totalLanes > 1) {
      const mappedRoi = toVideoRect(roi);
      const rx = offsetX + Number(mappedRoi.x) * scaleX;
      const ry = offsetY + Number(mappedRoi.y) * scaleY;
      const rw = Number(mappedRoi.w) * scaleX;
      const rh = Number(mappedRoi.h) * scaleY;
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 2;
      ctx.strokeRect(rx, ry, rw, rh);
      ctx.lineWidth = 1.5;
      for (let i = 1; i < totalLanes; i += 1) {
        const lineX = rx + (rw * i) / totalLanes;
        ctx.beginPath();
        ctx.moveTo(lineX, ry);
        ctx.lineTo(lineX, ry + rh);
        ctx.stroke();
      }
      ctx.font = 'bold 11px Arial';
      ctx.fillStyle = '#00ff00';
      for (let i = 0; i < totalLanes; i += 1) {
        const label = formatZoneLabel(zoneNoun, i + 1);
        const textWidth = ctx.measureText(label).width;
        const laneCenterX = rx + (rw * (i + 0.5)) / totalLanes;
        ctx.fillText(label, laneCenterX - textWidth / 2, ry + 14);
      }
    }
  }, [imgRef, roi, laneRois, laneCount, zoneNoun, hasRoi, workstationMeta, sourceFrameSize, hoveredLaneKey]);

  useEffect(() => {
    if (!shouldShow || workstationMeta == null) {
      setHoveredLaneKey(null);
      return undefined;
    }
    const pointIn = (box, x, y) =>
      box && x >= box.x && y >= box.y && x <= box.x + box.w && y <= box.y + box.h;
    const onMove = (event) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const bounds = canvas.getBoundingClientRect();
      if (!(bounds.width > 0) || !(bounds.height > 0)) return;
      const x = ((event.clientX - bounds.left) / bounds.width) * canvas.width;
      const y = ((event.clientY - bounds.top) / bounds.height) * canvas.height;
      let next = null;
      hitRegionsRef.current.forEach((region) => {
        if (pointIn(region.roi, x, y) || pointIn(region.label, x, y) || pointIn(region.detail, x, y)) {
          next = region.key;
        }
      });
      setHoveredLaneKey((prev) => (prev === next ? prev : next));
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [shouldShow, workstationMeta]);

  useEffect(() => {
    if (!shouldShow) return undefined;
    let raf = 0;
    let running = true;
    const tick = () => {
      if (!running) return;
      drawOverlay();
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    const onResize = () => drawOverlay();
    window.addEventListener('resize', onResize);
    return () => {
      running = false;
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [shouldShow, drawOverlay]);

  if (!shouldShow) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default PreviewRoiOverlay;
